import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkedAlt, FaMapPin, FaCity, FaGlobe, FaChartLine, FaUsers, FaTicketAlt } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import { logout } from '../../../../utils/api/auth';
import OverviewCards from '../../../../components/OverviewCards.jsx';
import PieChartCard from '../../../../components/PieChartCard.jsx';
import CollapsibleTable from '../../../../components/CollapsibleTable.jsx';
import EventWorldMap from '../../../../components/EventWorldMap.jsx';
import { fetchOrganiserGeographicEventDistribution } from '../../../../utils/api/organiserApi.js';
import '../css/GeographicEventDistribution.css';
import '../../AdminDash/css/AdminDash.css';
import '../../../../components/css/AdminAnalytics.css';

function GeographicEventDistribution() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({
    totalLocations: 0,
    topCities: [],
    regionalPerformance: [],
    eventDistribution: [],
    venueTypes: [],
    geographicInsights: []
  });

  useEffect(() => {
    fetchGeographicData();
  }, []);

  const fetchGeographicData = async () => {
    try {
      setLoading(true);
      
      console.log('🗺️ Frontend: Fetching geographic data...');
      
      // Fetch real data from API
      const data = await fetchOrganiserGeographicEventDistribution();
      
      console.log('🗺️ Frontend: Received data:', data);
      console.log('🗺️ Frontend: Data keys:', Object.keys(data));
      console.log('🗺️ Frontend: totalLocations:', data.totalLocations);
      console.log('🗺️ Frontend: topCities:', data.topCities);
      console.log('🗺️ Frontend: totalAttendance:', data.totalAttendance);
      console.log('🗺️ Frontend: growthLeader:', data.growthLeader);
      console.log('🗺️ Frontend: eventDistribution:', data.eventDistribution);
      console.log('🗺️ Frontend: regionalPerformance:', data.regionalPerformance);
      console.log('🗺️ Frontend: venueTypes:', data.venueTypes);
      console.log('🗺️ Frontend: heatmapData:', data.heatmapData);
      
      // Process the data to match the expected format
      const processedData = {
        totalLocations: data.totalLocations || 0,
        topCities: data.topCities || [],
        regionalPerformance: data.regionalPerformance || [],
        eventDistribution: data.eventDistribution || [],
        venueTypes: data.venueTypes || [],
        geographicInsights: data.geographicInsights || [],
        totalAttendance: data.totalAttendance || 0,
        growthLeader: data.growthLeader || null,
        heatmapData: data.heatmapData || []
      };

      console.log('🗺️ Frontend: Processed data:', processedData);

      setAnalyticsData(processedData);
    } catch (error) {
      console.error('🗺️ Frontend: Error fetching geographic data:', error);
      // Set empty data on error
      setAnalyticsData({
        totalLocations: 0,
        topCities: [],
        regionalPerformance: [],
        eventDistribution: [],
        venueTypes: [],
        geographicInsights: [],
        totalAttendance: 0,
        growthLeader: null,
        heatmapData: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout(nav);
  };

  // Prepare data for OverviewCards component
  const overviewCards = [
    {
      icon: <FaMapPin />,
      title: 'Total Locations',
      value: analyticsData.totalLocations,
      subtitle: 'Cities with events',
      color: '#0786f5ff'
    },
    {
      icon: <FaCity />,
      title: 'Top City',
      value: analyticsData.topCities[0]?.city || 'No data',
      subtitle: `${analyticsData.topCities[0]?.events || 0} events`,
      color: '#0786f5ff'
    }
  ];

  // Prepare data for PieChartCard component
  const pieChartData = analyticsData.eventDistribution.map(item => ({
    name: item.name,
    value: item.value,
    percentage: item.percentage
  }));
  
  // Generate colors for pie chart
  const pieChartColors = analyticsData.eventDistribution.map((_, index) => {
    const colors = ['#667eea', '#f5576c', '#4facfe', '#00b894', '#f7b731', '#ff6b6b'];
    return colors[index % colors.length];
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Prepare data for CollapsibleTable component
  const topCitiesColumns = [
    { 
      header: 'City', 
      key: 'city',
      render: (value) => (
        <div className="city-name">
          <span className="location-icon">📍</span>
          {value}
        </div>
      )
    },
    { header: 'Events', key: 'events' },
    { header: 'Total Attendance', key: 'attendance', render: (value) => value.toLocaleString() },
    { header: 'Total Revenue', key: 'revenue', render: (value) => formatCurrency(value) },
    { 
      header: 'Avg per Event', 
      key: 'revenue',
      render: (value, row) => formatCurrency(Math.round(value / row.events))
    },
    { 
      header: 'Growth Rate', 
      key: 'growth',
      render: (value) => {
        if (value === 0 || !value) {
          return <span className="growth-rate low">N/A</span>;
        }
        return (
          <span className={`growth-rate ${value > 10 ? 'high' : value > 5 ? 'medium' : 'low'}`}>
            +{value}%
          </span>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div className="geographic-event-distribution">
        <GlobalHeader
          title="Geographic Event Distribution"
          subtitle="Loading geographic data..."
          onLogout={handleLogout}
          backTo="/organiser-dash"
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="geographic-event-distribution">
      <GlobalHeader
        title="Geographic Event Distribution"
        subtitle="Event distribution by location and regional performance"
        onLogout={handleLogout}
        backTo="/organiser-dash"
      />

      <main className="geographic-event-distribution__main">
        {/* Overview Cards */}
        <OverviewCards cards={overviewCards} />

        {/* Regional Map */}
        <section className="regional-map">
          <h3>Regional Event Heatmap</h3>
          <EventWorldMap events={analyticsData.heatmapData} />
        </section>

      </main>
    </div>
  );
}

export default GeographicEventDistribution;
